
import { Box } from "@mui/material";

import { useAuth } from "@/shared/stores/useAuth";

import type { ImpactCardData } from "../utils/impactCardData";

import { useImpactCardContributions } from "../hooks/useImpactCardContributions";
import { useImpactLocalPartner } from "../hooks/useImpactLocalPartner";
import { useAnchorCubes } from "../hooks/useAnchorCubes";

import { findCubeForSection } from "./ImpactCubePage";

import ImpactCausePage from "./ImpactCausePage";
import ImpactFundingPage from "./ImpactFundingPage";
// Local partners are fixed to AOL Nepal and the PM Relief Fund for now, so the
// invite-a-partner page stays out of the book. Restore with the "people" section.
// import ImpactPeoplePage from "./ImpactPeoplePage";
import ImpactRecipientPage from "./ImpactRecipientPage";

import ImpactContributePage, {
  ImpactContributionStatusPage,
} from "./ImpactContributePage";

import ImpactThankYouPage from "./ImpactThankYouPage";
import ImpactAdminContributorsPage from "./ImpactAdminContributorsPage";

/* -------------------------------------------------------------------------- */
/* Sections and role-specific page order                                      */
/* -------------------------------------------------------------------------- */

export type ImpactCardBookSection =
  | "overview"
  | "funding"
  | "recipientPm"
  | "recipientAol"
  | "contribute"
  | "contributeStatus"
  | "thanks"
  | "adminContributors"
  | "people";

/**
 * Collector order remains unchanged.
 */
export const IMPACT_COLLECTOR_PAGE_ORDER: ImpactCardBookSection[] = [
  "overview",
  "funding",
  "recipientPm",
  "recipientAol",
  "contribute",
  "contributeStatus",
  "thanks",
];

/**
 * The creator opens the book on the admin contributions page.
 * "people" (invite a local partner) is disabled while AOL Nepal and the
 * PM Relief Fund are the fixed local partners.
 */
export const IMPACT_CREATOR_PAGE_ORDER: ImpactCardBookSection[] = [
  "adminContributors",
  "overview",
  "funding",
  "recipientPm",
  "recipientAol",
  "thanks",
  // "people",
];

/**
 * Partner order is separate from creator and collector.
 */
export const IMPACT_PARTNER_PAGE_ORDER: ImpactCardBookSection[] = [
  "overview",
  "funding",
  "recipientPm",
  "recipientAol",
  "thanks",
];

export function getImpactCardBookPageOrder({
  isCreator,
  isPartner,
}: {
  isCreator: boolean;
  isPartner: boolean;
}): ImpactCardBookSection[] {
  if (isCreator) {
    return IMPACT_CREATOR_PAGE_ORDER;
  }

  if (isPartner) {
    return IMPACT_PARTNER_PAGE_ORDER;
  }

  return IMPACT_COLLECTOR_PAGE_ORDER;
}

/* -------------------------------------------------------------------------- */
/* Props                                                                      */
/* -------------------------------------------------------------------------- */

type ImpactCardBookPageProps = {
  section: ImpactCardBookSection;

  data: ImpactCardData;

  title: string;
  description?: string;
  talentName?: string;

  isLoggedIn: boolean;
  isOwner: boolean;
  isCollected?: boolean;

  onSignIn?: () => void;
  onNavigate?: (section: ImpactCardBookSection) => void;

  mainCardId?: number;
  cardTalentId?: number | string;
  purchaseId?: number | string | null;

  cardContent?: unknown;
  coverImageUrl?: string;

  stackWithParent?: boolean;
  /** True when this page’s `[data-impact-book-scroll='active']` is the visible book page. */
  bookScrollActive?: boolean;
};

const c = {
  ivory: "#FAF8F2",
  text: "#17231D",
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function ImpactCardBookPage({
  section,
  data: rawData,
  title,
  description,
  talentName,
  isLoggedIn,
  isOwner,
  isCollected = false,
  onSignIn,
  onNavigate,
  mainCardId,
  cardTalentId,
  purchaseId,
  cardContent,
  coverImageUrl,
  stackWithParent = false,
  bookScrollActive = true,
}: ImpactCardBookPageProps) {
  const { userInfo } = useAuth();

  /* ---------------------------------------------------------------------- */
  /* Live contribution data                                                 */
  /* ---------------------------------------------------------------------- */

  const live = useImpactCardContributions({
    mainCardId,
    cardTalentId,
    purchaseId,
  });

  const data = live.applyTo(rawData);

  /* ---------------------------------------------------------------------- */
  /* Creator / partner role                                                 */
  /* ---------------------------------------------------------------------- */

  const field = useImpactLocalPartner({
    mainCardId,
    cardTalentId,
    cardContent,
    data,
  });

  const { cubes } = useAnchorCubes();

  const isCreator = field.isCreator;
  const isPartner = field.isPartner;

  const viewerUserId = Number(userInfo?.id) || undefined;

  /* ---------------------------------------------------------------------- */
  /* Page content                                                           */
  /* ---------------------------------------------------------------------- */

  const sectionContent = (() => {
    /* -------------------------------------------------------------------- */
    /* Creator: all contributions                                           */
    /* -------------------------------------------------------------------- */

    if (section === "adminContributors") {
      if (!isCreator) {
        return null;
      }

      return (
        <ImpactAdminContributorsPage
          data={data}
          verifyingId={live.verifyingId}
          onVerify={live.verify}
        />
      );
    }

    /* -------------------------------------------------------------------- */
    /* Overview                                                             */
    /* -------------------------------------------------------------------- */

    if (section === "overview") {
      return (
        <ImpactCausePage
          title={title}
          description={description}
          data={data}
          coverImageUrl={coverImageUrl}
        />
      );
    }

    /* -------------------------------------------------------------------- */
    /* Funding                                                              */
    /* -------------------------------------------------------------------- */

    if (section === "funding") {
      return (
        <ImpactFundingPage
          data={data}
          title={title}
          description={description}
          coverImageUrl={coverImageUrl}
          onNavigate={(next) => onNavigate?.(next)}
        />
      );
    }

    /* -------------------------------------------------------------------- */
    /* Nepal PM Relief Fund                                                 */
    /* -------------------------------------------------------------------- */

    if (section === "recipientPm") {
      return (
        <ImpactRecipientPage
          cube={findCubeForSection(cubes, "recipientPm")}
          recipient={data.fundRecipients.find(
            (item) => item.sectionKey !== "recipientAol"
          )}
          fallbackTitle="Nepal Prime Minister's Relief Fund"
          fallbackRole="정부 주도의 재난 구호 지원"
          variant="pm"
        />
      );
    }

    /* -------------------------------------------------------------------- */
    /* Art of Living Nepal                                                  */
    /* -------------------------------------------------------------------- */

    if (section === "recipientAol") {
      return (
        <ImpactRecipientPage
          cube={findCubeForSection(cubes, "recipientAol")}
          recipient={data.fundRecipients.find(
            (item) => item.sectionKey === "recipientAol"
          )}
          fallbackTitle="Art of Living Nepal"
          fallbackRole="사람을 치유하고 회복력 있는 공동체를 만듭니다."
          variant="aol"
        />
      );
    }

    /* -------------------------------------------------------------------- */
    /* Collector: contribute                                                */
    /* -------------------------------------------------------------------- */

    if (section === "contribute") {
      if (isCreator || isPartner) {
        return null;
      }

      return (
        <ImpactContributePage
          data={data}
          title={title}
          creatorName={talentName}
          isLoggedIn={isLoggedIn}
          isOwner={isOwner}
          isCollected={isCollected}
          viewerUserId={viewerUserId}
          onSignIn={onSignIn}
          onSubmit={mainCardId ? live.contribute : undefined}
          submitting={live.isSubmitting}
          onOpenStatus={() => onNavigate?.("contributeStatus")}
        />
      );
    }

    /* -------------------------------------------------------------------- */
    /* Collector: contribution status                                       */
    /* -------------------------------------------------------------------- */

    if (section === "contributeStatus") {
      if (isCreator || isPartner) {
        return null;
      }

      return (
        <ImpactContributionStatusPage
          data={data}
          isLoggedIn={isLoggedIn}
          isOwner={isOwner}
          viewerUserId={viewerUserId}
        />
      );
    }

    /* -------------------------------------------------------------------- */
    /* Thank-you page                                                       */
    /* -------------------------------------------------------------------- */

    if (section === "thanks") {
      return (
        <ImpactThankYouPage
          data={data}
          cube={findCubeForSection(cubes, "thanks")}
          isPartner={isPartner}
          isCreator={isCreator}
          mainCardId={mainCardId}
          bookScrollActive={bookScrollActive}
        />
      );
    }

    /* -------------------------------------------------------------------- */
    /* Creator: local partner management (disabled)                         */
    /* -------------------------------------------------------------------- */

    // The local partners are AOL Nepal and the PM Relief Fund, so nobody is
    // invited from inside the Card yet. Re-enable together with the "people"
    // entry in IMPACT_CREATOR_PAGE_ORDER and the ImpactPeoplePage import.
    //
    // if (section === "people") {
    //   if (!isCreator) {
    //     return null;
    //   }
    //
    //   return (
    //     <ImpactPeoplePage
    //       data={data}
    //       coverImageUrl={coverImageUrl}
    //       isCreator={isCreator}
    //       savingPartner={field.saving}
    //       partner={field.partner}
    //       onAssignPartner={field.assignPartner}
    //       onUnassignPartner={field.unassignPartner}
    //     />
    //   );
    // }

    return null;
  })();

  /* ---------------------------------------------------------------------- */
  /* Layout                                                                 */
  /* ---------------------------------------------------------------------- */

  const fillSheet = section === "contributeStatus";

  const stackedRecipient =
    stackWithParent &&
    (section === "recipientPm" || section === "recipientAol");

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: stackWithParent ? "100%" : 0,

        overflowY: fillSheet ? "hidden" : stackedRecipient ? "visible" : "auto",
        /* Pages are full-bleed in places (hero images); never let a stray
           child widen the sheet and cut content off on a phone. */
        overflowX: "hidden",
        display: fillSheet ? "flex" : "block",
        flexDirection: "column",

        bgcolor: c.ivory,
        color: c.text,

        px: { xs: 2.25, sm: 3 },
        py: { xs: 2.5, sm: 3.25 },

        /* Long amounts, e-mails, and URLs must wrap rather than overflow. */
        overflowWrap: "break-word",

        "&::-webkit-scrollbar": {
          width: 5,
        },

        "&::-webkit-scrollbar-thumb": {
          bgcolor: "rgba(16,44,36,.16)",
          borderRadius: 999,
        },
      }}
    >
      {sectionContent}
    </Box>
  );
}